import { AmendsChangeType } from "authoring/lib/enum/amends-change-type.mjs"
import { getFrequencyLabel } from "library/get-frequency-label.mjs"

export function buildAuthoringRecommendationComparison(recommendation = {}) {
    switch (`${recommendation?.changeType || ""}`) {
        case "schedule_title":
            return buildTitleComparison({
                label: "Schedule Title",
                oldText: recommendation?.oldText,
                newText: recommendation?.newText,
            })
        case "intro_content":
            return buildRichTextComparison({
                key: "content",
                label: "Introduction",
                oldText: recommendation?.oldText,
                newText: recommendation?.newText,
            })
        case "intro_note":
            return buildRichTextComparison({
                key: "note",
                label: "Introduction Notes",
                oldText: recommendation?.oldText,
                newText: recommendation?.newText,
            })
        case "task_content":
            return buildRichTextComparison({
                key: "content",
                label: "Task Content",
                oldText: recommendation?.oldText,
                newText: recommendation?.newText,
            })
        case "task_note":
            return buildRichTextComparison({
                key: "note",
                label: "Task Notes",
                oldText: recommendation?.oldText,
                newText: recommendation?.newText,
            })
        case "task_title":
            return buildTitleComparison({
                label: "Task Title",
                oldText: recommendation?.oldText,
                newText: recommendation?.newText,
            })
        case "task_frequency":
            return buildFrequencyComparison(recommendation)
        case "add_task":
            return buildAddedTaskComparison(recommendation)
        case "remove_task":
            return buildRemovedTaskComparison(recommendation)
        default:
            return null
    }
}

function buildTitleComparison({ label, oldText, newText }) {
    const originalTitle = `${oldText || ""}`.trim()
    const tailoredTitle = `${newText || ""}`.trim()

    return {
        auditKeys: ["title"],
        original: { title: originalTitle },
        tailored: { title: tailoredTitle },
        plainComparison: {
            label,
            originalValue: originalTitle,
            tailoredValue: tailoredTitle,
        },
        contentComparison: null,
        noteComparison: null,
    }
}

function buildRichTextComparison({ key, label, oldText, newText }) {
    const originalValue = `${oldText || ""}`
    const tailoredValue = `${newText || ""}`

    return {
        auditKeys: [key],
        original: { [key]: originalValue },
        tailored: { [key]: tailoredValue },
        plainComparison: null,
        contentComparison:
            key === "content"
                ? {
                      label,
                      originalValue,
                      tailoredValue,
                      isNew: false,
                  }
                : null,
        noteComparison:
            key === "note"
                ? {
                      label,
                      originalValue,
                      tailoredValue,
                      isNew: false,
                  }
                : null,
    }
}

function buildFrequencyComparison(recommendation) {
    const originalLabel = `${recommendation?.oldText || ""}`.trim()
    const tailoredLabel = buildFrequencyLabel(recommendation?.newFrequency)

    return {
        auditKeys: ["frequency"],
        original: { frequency: { label: originalLabel } },
        tailored: { frequency: { label: tailoredLabel } },
        plainComparison: {
            label: "Task Frequency",
            originalValue: originalLabel,
            tailoredValue: tailoredLabel,
        },
        contentComparison: null,
        noteComparison: null,
    }
}

function buildAddedTaskComparison(recommendation) {
    const task = normalizeProposedTask(recommendation?.proposedTask)
    if (!task) return null

    return {
        auditKeys: buildAddedTaskAuditKeys(task),
        original: buildEmptyAddedTaskOriginal(task),
        tailored: { ...task, changeType: AmendsChangeType.ADDED },
        plainComparison: {
            label: "New Task Title",
            originalValue: "",
            tailoredValue: task.title,
            isNew: true,
        },
        contentComparison: buildOptionalNewRichTextComparison("Task Content", task.content),
        noteComparison: buildOptionalNewRichTextComparison("Task Notes", task.note),
    }
}

function buildRemovedTaskComparison(recommendation) {
    const removedText = `${recommendation?.oldText || ""}`.trim()
    const taskTitle = `${recommendation?.taskTitle || ""}`.trim() || "Removed Task"

    return {
        auditKeys: ["changeType"],
        original: { changeType: AmendsChangeType.REMOVED },
        tailored: { changeType: AmendsChangeType.REMOVED },
        plainComparison: {
            label: taskTitle,
            originalValue: removedText,
            tailoredValue: "",
        },
        contentComparison: null,
        noteComparison: null,
    }
}

function buildAddedTaskAuditKeys(task) {
    const keys = ["changeType", "title"]
    if (task?.classification) keys.push("classification")
    if (task?.frequency?.label) keys.push("frequency")
    return keys
}

function buildEmptyAddedTaskOriginal() {
    return {
        changeType: AmendsChangeType.ADDED,
        title: "",
        classification: "",
        frequency: { label: "" },
        content: "",
        note: "",
    }
}

function buildOptionalNewRichTextComparison(label, value) {
    const text = `${value || ""}`
    if (!text.trim()) return null

    return {
        label,
        originalValue: "",
        tailoredValue: text,
        isNew: true,
    }
}

function normalizeProposedTask(task) {
    if (!task || typeof task !== "object") return null

    return {
        title: `${task?.title || task?.taskTitle || "New task"}`.trim(),
        content: `${task?.content || ""}`,
        note: `${task?.note || ""}`,
        classification: `${task?.classification || "Amber"}`.trim(),
        frequency: { label: buildFrequencyLabel(task?.frequency) },
    }
}

function buildFrequencyLabel(frequency) {
    if (!frequency) return ""
    if (typeof frequency === "string") return frequency.trim()
    if (typeof frequency === "object" && "label" in frequency) {
        return `${frequency.label || ""}`.trim()
    }
    if (typeof frequency === "object" && "period" in frequency) {
        return `${getFrequencyLabel(frequency) || ""}`.trim()
    }
    return ""
}
